import { isAccessProduct, isJSMProduct, isStatuspage } from '@atlassian/cssxp-utils-product';
import { generatedTypesNew, generatedTypesSxp } from '@css-xp/graphtypes';
import {
  ROVO_PRODUCT_ID,
  SUBSCRIPTION_CONSTANTS,
  APPS_SUPPORTABLE_PRODUCTS,
  STATUSPAGE_PRODUCT_ID,
} from '@css-xp/ppc-constants';
import { isRovoProduct } from './entitlement-utils';

export enum EntitlementEdition {
  ENTERPRISE = 'ENTERPRISE',
  ENTERPRISE_INSTANCE = 'ENTERPRISE INSTANCE',
  ADVANCED = 'ADVANCED',
}

export enum ProductRelationShipType {
  ADD_ON = 'ADD_ON',
  APP = 'APP',
}

export const MultiInstanceRelationshipPathType = {
  MULTI_INSTANCE: 'MULTI_INSTANCE',
};

export enum BillingBehaviourType {
  PAUSE_BILLING = 'PAUSE_BILLING',
}

export const ATLASSIAN_GUARD_PREMIUM_PRODUCT_ID = '17e4ba8d-5eba-43d4-bb6b-07dd3824b06f';
export const JSM_PREMIUM_OFFERING_ID = '0c6683b4-4633-4673-beca-690d171de6a5';
export const EXTRA_OBJECT_ADD_ON_PRODUCT_ID = 'd869ebdc-8a57-4526-92a6-1177070b651b';
export const EXTRA_ASSISTED_CONVERSATION_ADD_ON_PRODUCT_ID = 'd507d4e4-49b0-4b87-9905-d20993607a56';

export const MULTI_INSTANCE_APP_OFFERING_IDS = [
  'e08c6652-23e9-4f62-bc99-1161efe6e255', // Standard multi-instance
  'ace30d70-2235-4626-9004-c873af17577a', // Advanced multi-instance
  '05708245-79ba-49d8-ab01-d6213b998233',
  '882ee168-d35d-45c1-b403-db2ed8b75c3a',
  'ecb111b3-bf7e-431e-a889-c7fdcc9516a1',
  '380c72d6-1c90-4e78-b770-48e1d9e3eda6',
  '9d8f920f-4e7b-4af9-a598-540aaa2f693f',
  'a299a532-4262-4234-9c73-0c334176f465',
  '9d8f920f-4e7b-4af9-a598-540aaa2f693f',
];
export const MULTI_INSTANCE_APP_INSTANCE_OFFERING_IDS = [
  '6b34b5fb-ac0e-481c-82c5-c484eba28439', // Standard Instance Child App
  '18041bfe-94f7-40b0-bd43-3135a74a8eb4', // Advanced Instance Child App
  'e6937c9d-51ac-4988-b783-1d8f0292e773',
  '058fdee5-6266-43eb-98e6-fc00563232fb',
  '07c2ab67-2615-4efc-94e8-6bd9f3c5eb86',
  'c265700e-b1a0-4f27-884b-14c7c41fd485',
  'e27b79bd-48e1-48c2-a010-59c97d1787ae',
  '5cb8c5cd-aadd-4d7d-b5f0-3564e2aea0fc',
];

export const STATUSPAGE_ADDON_OFFERING_IDS = {
  PAGE_VIEWER_SSO: 'd6d88225-7501-437d-80dc-3d00207a9d80',
  TEAM_MEMBERS: 'f8010902-5456-11ea-8d77-2e728ce88125',
  METRICS: 'c5d47eac-5459-11ea-8d77-2e728ce88125',
  SUBSCRIBERS: '23b3e0e4-545a-11ea-8d77-2e728ce88125',
};

export const isEnterpriseOffering = (offeringName: string | null | undefined) =>
  offeringName?.toUpperCase() === 'ENTERPRISE';

export const isAddOnOffering = (offeringName: string | null | undefined) => offeringName?.toUpperCase() === 'ADD-ON';

export const isEnterpriseInstanceOffering = (offeringName: string | null | undefined) =>
  offeringName?.toUpperCase() === EntitlementEdition.ENTERPRISE_INSTANCE;

export function isAddOns(productId?: string): boolean {
  if (productId) {
    return [EXTRA_OBJECT_ADD_ON_PRODUCT_ID, EXTRA_ASSISTED_CONVERSATION_ADD_ON_PRODUCT_ID].includes(productId);
  }
  return false;
}

// List of product ID's and offerings eligible for Add-On products tab.
export const ADD_ON_PRODUCTS = [
  {
    productId: 'de63a248-1f93-46ed-b6ea-ab8e38af1c88', // JSM
    offeringId: JSM_PREMIUM_OFFERING_ID, // edition : premium
  },
  {
    productId: 'ad8473d4-3a4d-4e36-827c-0a893439e9fa', // Atlassian Guard,
    offeringId: '1ac3b7dd-7141-414e-bc00-c890acc458b0', // edition : standard
  },
];

export enum ChargeElementText {
  USER = 'users',
  AGENT = 'agents',
  OBJECT = 'objects',
  VIRTUAL_AGENT = 'assisted conversations',
  PRODUCT_USER = 'product users',
  UNIT = 'units',
}

export enum ChargeElementType {
  USER = 'user',
  AGENT = 'agent',
  OBJECT = 'object',
  VIRTUAL_AGENT = 'conversation',
  PRODUCT_USER = 'product-user',
  UNIT = 'unit',
}

export const getChargeElementText = (chargeElement: string | undefined | null) => {
  switch (chargeElement) {
    case ChargeElementType.OBJECT:
      return ChargeElementText.OBJECT;
    case ChargeElementType.VIRTUAL_AGENT:
      return ChargeElementText.VIRTUAL_AGENT;
    case ChargeElementType.AGENT:
      return ChargeElementText.AGENT;
    default:
      return ChargeElementText.USER;
  }
};

export const getLinkedEntitlementChargeElement = (productId: string | undefined | null) => {
  switch (productId) {
    case EXTRA_OBJECT_ADD_ON_PRODUCT_ID:
      return ChargeElementType.OBJECT;
    case EXTRA_ASSISTED_CONVERSATION_ADD_ON_PRODUCT_ID:
      return ChargeElementType.VIRTUAL_AGENT;
    case ATLASSIAN_GUARD_PREMIUM_PRODUCT_ID:
      return ChargeElementType.PRODUCT_USER;
    case STATUSPAGE_PRODUCT_ID:
      return ChargeElementType.UNIT;
    default:
      return ChargeElementType.USER;
  }
};

export const isAtlassianGuardPremiumProduct = (productId: string | null | undefined) =>
  productId === ATLASSIAN_GUARD_PREMIUM_PRODUCT_ID;

export const isRovoInstanceProduct = (
  product: generatedTypesNew.TransactionAccountProduct | generatedTypesNew.EntitlementDetails
) => {
  if (product.productId !== ROVO_PRODUCT_ID) {
    return false;
  }
  const rovoInstanceEntitlement = !!product?.relatesFromEntitlements?.some(
    (entitlementRelation) => entitlementRelation?.relationshipType === MultiInstanceRelationshipPathType.MULTI_INSTANCE
  );
  return rovoInstanceEntitlement;
};

export const isRovoOrgProduct = (
  product: generatedTypesNew.TransactionAccountProduct | generatedTypesNew.EntitlementDetails
) => {
  return !(product?.productId !== ROVO_PRODUCT_ID || isRovoInstanceProduct(product));
};

export const isCollectionProductOffering = (
  offering: Pick<generatedTypesNew.Offering, 'relationshipRequirements'> | undefined | null
) => {
  return !!offering?.relationshipRequirements?.some(
    (rel) => rel?.side === 'FROM' && rel?.type === SUBSCRIPTION_CONSTANTS.COLLECTION_ENTITLEMENT_TYPE
  );
};

export const isOrgProduct = ({
  productIdOrKey,
  offeringName,
  relatesFromEntitlements,
  offering,
}: {
  productIdOrKey?: generatedTypesNew.Maybe<string> | undefined;
  offeringName?: generatedTypesNew.Maybe<string> | undefined;
  relatesFromEntitlements?:
    | generatedTypesNew.Maybe<Array<generatedTypesNew.Maybe<generatedTypesNew.RelatedEntitlementDetails>>>
    | generatedTypesNew.Maybe<Array<generatedTypesNew.Maybe<generatedTypesSxp.LineItemRelation>>>
    | undefined;
  offering?: generatedTypesNew.Maybe<generatedTypesNew.Offering> | undefined;
  relatesToEntitlements?:
    | generatedTypesNew.Maybe<Array<generatedTypesNew.Maybe<generatedTypesNew.RelatedEntitlementDetails>>>
    | generatedTypesNew.Maybe<Array<generatedTypesNew.Maybe<generatedTypesSxp.LineItemRelation>>>
    | undefined;
}) => {
  return (
    isEnterpriseOffering(offeringName) ||
    isAccessProduct(productIdOrKey) ||
    isRovoOrgProduct({
      productId: productIdOrKey,
      relatesFromEntitlements: relatesFromEntitlements,
    } as generatedTypesNew.EntitlementDetails) ||
    isCollectionProductOffering(offering) ||
    isMultiInstanceProductOffering(offering)
  );
};

export const getProductsSearchableRelationshipTypes = (productId: string) => {
  const isAppSupportableProduct = APPS_SUPPORTABLE_PRODUCTS.includes(productId);
  const productSearchCapability = [];
  if (isJSMProduct(productId) || isStatuspage(productId)) {
    productSearchCapability.push(ProductRelationShipType.ADD_ON);
  }
  if (isAppSupportableProduct) {
    productSearchCapability.push(ProductRelationShipType.APP);
  }
  return productSearchCapability;
};

export const getAddOnQuantity = (
  billingCycle: string | undefined | null,
  activeUsers: number | undefined | null,
  userTier: number | undefined | null
) => {
  if (billingCycle === generatedTypesNew.BillingFrequency.ANNUAL) {
    return userTier ?? 0;
  }
  return activeUsers ?? 0;
};

export const isFutureDatedInactiveSubscription = (
  isFutureDatedSubscription?: boolean,
  currentBillingStatus?: generatedTypesNew.BillingStatus
) => {
  return !!(isFutureDatedSubscription && currentBillingStatus === generatedTypesNew.BillingStatus.INACTIVE);
};

export const isParentAppInstance = (offeringId: string | null | undefined) => {
  if (!offeringId) return false;

  return MULTI_INSTANCE_APP_OFFERING_IDS.includes(offeringId);
};

// parent app instance
export const isMultiInstanceProductOffering = (
  offering?: generatedTypesNew.Offering | generatedTypesSxp.LineItemOffering | generatedTypesSxp.Offering | null,
  productId?: string | null
) => {
  if (productId) {
    return (
      !!offering?.relationshipRequirements?.find(
        (relationship: generatedTypesNew.RelationshipRequirement | null) =>
          relationship?.level === 'ALLOWED' &&
          relationship?.side?.toUpperCase() === 'FROM' &&
          relationship?.type === MultiInstanceRelationshipPathType.MULTI_INSTANCE
      ) && !isRovoProduct(productId)
    );
  }
  return !!offering?.relationshipRequirements?.find(
    (relationship: generatedTypesNew.RelationshipRequirement | null) =>
      relationship?.level === 'ALLOWED' &&
      relationship?.side?.toUpperCase() === 'FROM' &&
      relationship?.type === MultiInstanceRelationshipPathType.MULTI_INSTANCE
  );
};

// child app instance
export const isMultiInstanceOffering = (
  offering?: generatedTypesNew.Offering | generatedTypesSxp.LineItemOffering | generatedTypesSxp.Offering | null,
  productId?: string | null
) => {
  if (productId) {
    return (
      !!offering?.relationshipRequirements?.find(
        (relation) =>
          relation?.side?.toUpperCase() === 'TO' && relation?.type === MultiInstanceRelationshipPathType.MULTI_INSTANCE
      ) && !isRovoProduct(productId)
    );
  }
  return !!offering?.relationshipRequirements?.find(
    (relation) =>
      relation?.side?.toUpperCase() === 'TO' && relation?.type === MultiInstanceRelationshipPathType.MULTI_INSTANCE
  );
};

export const isMultiInstanceAppInstance = (offeringId: string | null | undefined) => {
  if (!offeringId) return false;
  return MULTI_INSTANCE_APP_INSTANCE_OFFERING_IDS.includes(offeringId);
};

/**
 * we are converting productId = offeringId for statuspage addons and saving in store.
 * we are making this change because all statuspage addons have same product id
 * here is doc for more info https://hello.atlassian.net/wiki/spaces/~70121d05ec16fa6f7444db10a305ee7e0df3c/pages/5047030716/Statuspage+addons+support+in+UOF
 */
export const isStatusPageAddOnProduct = (productId?: string | null) => {
  if (productId) {
    return Object.values(STATUSPAGE_ADDON_OFFERING_IDS).includes(productId);
  }
  return false;
};
